import { createContext, PropsWithChildren } from 'react';
import {
  ActionOutboxEntityType,
  CatalogResults,
  EventWithData,
  Listing,
  ListingWithCatalogData,
  PurchaseOrderTicketGroup,
  Sale,
  SaleWithCatalogData,
} from 'src/WebApiController';
import { createContext as createContextWithSelector } from 'use-context-selector';

export type SearchDataWithEvent = {
  [key: string]: {
    sales: SaleWithCatalogData[] | null;
    salesSummary: SaleWithCatalogData | null;
    listings: ListingWithCatalogData[] | null;
    listingsSummary: ListingWithCatalogData | null;
    ticketGroups: PurchaseOrderTicketGroup[] | null;
    ticketGroupsSummary: PurchaseOrderTicketGroup | null;
    failedToRetrieveData: boolean;
  };
};

export type SearchDataSummary = {
  salesSummary: SaleWithCatalogData | null;
  listingsSummary: ListingWithCatalogData | null;
  ticketGroupsSummary: PurchaseOrderTicketGroup | null;
};

export type EntitySearchResult = {
  data: SearchDataWithEvent;
  totalItemsCount?: number | null;
  /**
   * PIT ID for next page requests, null if not using PIT (Point-In-Time) pagination
   */
  pitId?: string | null;
  /**
   * Search after values for next page cursor (null if no more pages)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
  /**
   * Ordered list of event group IDs (ViagogoVirtualIds) in the order returned by OpenSearch.
   * Solves a JS serialization problem: Object.entries() reorders integer-like keys numerically
   * per ECMAScript spec, which would override the OpenSearch sort order. Use this to iterate
   * the data dictionary in the correct group order.
   * Note: coarser than sortedItemIds — gives the order of event groups, not individual items.
   */
  sortedGroupIds?: string[] | null;
  /**
   * Ordered list of individual item IDs (SaleId / ListingId) in the globally-sorted order
   * returned by OpenSearch. Finer-grained than sortedGroupIds: a single event group may contain
   * mixed-rank items (e.g., one event with both Transfer and Paper tickets) that appear out of
   * global sort order when flattened event-by-event. Use this to re-sort the flat list and
   * restore the correct global order for flattened/search views.
   */
  sortedItemIds?: number[] | null;
};

export type SearchDataContextProviderProps<TQuery> = PropsWithChildren<{
  queryKey: string;
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.Purchase;
  getSearchData?: (
    ids: string[],
    filterQuery: TQuery
  ) => Promise<EntitySearchResult>;
  getSearchDataSummary?: (
    ids: string[],
    filterQuery: TQuery
  ) => Promise<SearchDataSummary>;
  disabled?: boolean;
  /**
   * Increments each time the user explicitly clicks "Search". Including this in
   * the React Query key forces a refetch even when filter values are identical to
   * the previous submission. Scoped to each V2 route so client-side-only changes
   * (column sort, view toggle) do not trigger unnecessary server round-trips.
   *
   * Only applies to Inventory and Sale V2 routes that use SearchDataContextProvider.
   * Purchase V2 calls refetch() directly, so it does not need this counter.
   */
  searchSubmitVersion?: number;
}>;

export type ISearchDataContext = {
  isEnabled: boolean;
  isLoading: boolean;
  /** True while a background refetch is in-flight (e.g. sort/filter change). Unlike isLoading,
   *  this is true even when previous data is available, so consumers can show a subtle
   *  in-progress indicator without blanking the table. */
  isFetching: boolean;
  errorInfo?: { errorHeader: React.ReactNode; errorMessage: React.ReactNode };
  eventsTransformed?: EventWithData[];
  data?: CatalogResults;
  totalItemsCount?: number | null;
  /**
   * PIT ID for next page requests, null if not using PIT (Point-In-Time) pagination
   */
  pitId?: string | null;
  /**
   * Search after values for next page cursor (null if no more pages)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchAfter?: any[] | null;
  /**
   * Ordered item IDs in the globally-sorted order returned by OpenSearch.
   * Use this to restore global sort order when flattening event-grouped results.
   */
  sortedItemIds?: number[] | null;
  refetch: () => void;
  /** Performs a delayed refetch to account for OpenSearch indexing lag after bulk edits.
   *  Refetches after 2s, then again after 3 more seconds. */
  delayedRefetch: () => void;
  /** True while delayed refetch timers are active (between first and last refetch). */
  isRefreshingAfterBulkEdit: boolean;
  updateItemInEvent: (
    item: Listing | Sale | PurchaseOrderTicketGroup,
    entityType: ActionOutboxEntityType,
    sectionalUpdate?: boolean
  ) => void;
};

const emptyContext: ISearchDataContext = {
  isLoading: false,
  isFetching: false,
  isEnabled: false,
  refetch: () => {},
  delayedRefetch: () => {},
  isRefreshingAfterBulkEdit: false,
  totalItemsCount: null,
  pitId: null,
  searchAfter: null,
  sortedItemIds: null,
  updateItemInEvent: () => {},
};

export const SearchDataContext =
  createContext<ISearchDataContext>(emptyContext);

export const SearchDataContextWithSelector =
  createContextWithSelector<ISearchDataContext>(emptyContext);
